// FIRST, and by side effect: starts Sentry before the modules below are
// evaluated, which is what lets it patch fetch, history and the global error
// handlers. See ./instrument.ts.
import './instrument'

import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { reactErrorHandler } from '@sentry/react'
import { queryClient } from './lib/query-client'
import { installStaleDeployRecovery } from './lib/staleDeploy'
import './index.css'
import App from './App.tsx'

// Before the first render, because the chunk a deploy pulled out from under this
// tab may be requested by that render. Covers the preload path; `lazyPage` in
// App.tsx covers the import itself. See lib/staleDeploy.ts.
installStaleDeployRecovery()

// Overriding React's handlers replaces the default ones, and the default ones
// are what print the error to the console. Sentry is off in every local run and
// every test, so without this an uncaught render error becomes a blank page with
// a silent console — worse than what we had. Logged first, then reported, so
// `reactErrorHandler()` keeps its own `mechanism.handled: false` (passing it a
// callback would flip that to `true` and mislabel an uncaught crash as handled).
const reportUncaught = reactErrorHandler()
const reportRecoverable = reactErrorHandler()

createRoot(document.getElementById('root')!, {
  // React 19 hands the root these hooks for render errors, and they cover the
  // two cases an error boundary cannot.
  //
  // `onUncaughtError` is an error that reached the root with no boundary above
  // it — the blank page. `onRecoverableError` is one React retried and survived,
  // which never surfaces to the user at all and so would otherwise go unnoticed
  // entirely.
  //
  // `onCaughtError` is deliberately NOT wired here. That one fires for errors
  // our own `ErrorBoundary` catches, and the boundary already reports them
  // itself — it has to, because it needs the event id back in order to attach
  // whatever the person then types into the "what happened" form to that exact
  // event. Wiring it here as well would report every crash twice.
  onUncaughtError: (error, errorInfo) => {
    console.error('Uncaught error in React tree:', error, errorInfo.componentStack)
    reportUncaught(error, errorInfo)
  },
  onRecoverableError: (error, errorInfo) => {
    console.warn('React recovered from an error:', error, errorInfo.componentStack)
    reportRecoverable(error, errorInfo)
  },
}).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  </StrictMode>,
)
